"use client";

import { captureException } from "@sentry/nextjs";
import { useEffect, useSyncExternalStore } from "react";

const CRM_VIEW = "crm";
const CRM_VIEW_STORAGE_KEY = "crm_view";
const CRM_STEP_STORAGE_KEY = "crm_view_step";

export interface CrmView {
  isCrmView: boolean;
  step: string | null;
}

interface CrmViewEntry {
  step: string | null;
}

let crmViewKeptInMemory: CrmViewEntry | null = null;
let hasReportedUnreadableStorage = false;

function isInsideFrame(): boolean {
  return window.self !== window.top;
}

function readCrmViewFromUrl(): CrmViewEntry | null {
  const params = new URLSearchParams(window.location.search);
  if (params.get("view") !== CRM_VIEW) return null;
  return { step: params.get("step") };
}

function reportUnreadableStorageOnce(error: unknown) {
  if (hasReportedUnreadableStorage) return;
  hasReportedUnreadableStorage = true;
  captureException(error);
}

function readStoredCrmView(): CrmViewEntry | null {
  try {
    const storage = window.sessionStorage;
    if (storage.getItem(CRM_VIEW_STORAGE_KEY) !== CRM_VIEW) return null;
    return { step: storage.getItem(CRM_STEP_STORAGE_KEY) };
  } catch (error) {
    reportUnreadableStorageOnce(error);
    return null;
  }
}

function readRememberedCrmView(): CrmViewEntry | null {
  return crmViewKeptInMemory ?? readStoredCrmView();
}

function storeCrmView({ step }: CrmViewEntry) {
  try {
    const storage = window.sessionStorage;
    storage.setItem(CRM_VIEW_STORAGE_KEY, CRM_VIEW);
    if (step) {
      storage.setItem(CRM_STEP_STORAGE_KEY, step);
    } else {
      storage.removeItem(CRM_STEP_STORAGE_KEY);
    }
  } catch (error) {
    captureException(error);
  }
}

function rememberCrmView(entry: CrmViewEntry) {
  crmViewKeptInMemory = entry;
  storeCrmView(entry);
}

function readCrmViewEntry(): CrmViewEntry | null {
  if (!isInsideFrame()) return null;
  return readCrmViewFromUrl() ?? readRememberedCrmView();
}

function readIsCrmView(): boolean {
  return readCrmViewEntry() !== null;
}

function readCrmStep(): string | null {
  return readCrmViewEntry()?.step ?? null;
}

function readStandaloneIsCrmView(): boolean {
  return false;
}

function readStandaloneStep(): null {
  return null;
}

function subscribeToNothing(): () => void {
  return () => {};
}

export function useCrmView(): CrmView {
  const isCrmView = useSyncExternalStore(
    subscribeToNothing,
    readIsCrmView,
    readStandaloneIsCrmView,
  );
  const step = useSyncExternalStore(
    subscribeToNothing,
    readCrmStep,
    readStandaloneStep,
  );

  useEffect(() => {
    if (!isInsideFrame()) return;
    const entryFromUrl = readCrmViewFromUrl();
    if (entryFromUrl) rememberCrmView(entryFromUrl);
  }, []);

  return { isCrmView, step };
}
